import {Vector3} from 'three';
import {useARStore} from 'stores';
import {POS_SKY, xrStore} from 'lib';

import hitTestFromScreen from '../model/hitTestFromScreen';
import raycastCamHeightPoint from '../model/raycastCamHeightPoint';

type MoveType = {
  targetPosHelper: Vector3;
  posHelper: Vector3;
  lastKnownPosHelper: Vector3;
  run: () => Promise<Vector3 | undefined>;
};

/**
 * Detects 1 finger location on screen for hit testing.
 * @returns Vector3 position.
 */

const move: MoveType = {
  targetPosHelper: new Vector3(),
  posHelper: new Vector3(),
  lastKnownPosHelper: new Vector3(),
  async run() {
    const frame = await xrStore.requestFrame();
    const inputSources = xrStore.getState().session?.inputSources;
    if (!inputSources || inputSources.length !== 1 || !inputSources[0]) {
      return;
    }

    const baseSpace = xrStore.getState().originReferenceSpace;
    if (!baseSpace) {
      return;
    }

    const space = inputSources[0].targetRaySpace;
    const pose = frame.getPose(space, baseSpace);
    const pos = pose?.transform?.position;
    if (!pos) {
      return;
    }

    this.posHelper.set(pos.x, pos.y, pos.z);

    const camera = useARStore.getState().cameraRef.current;
    const {x: screenX, y: screenY} = this.posHelper.project(camera);
    this.targetPosHelper.copy(this.posHelper).unproject(camera);
    const hitTestTransforms = await hitTestFromScreen({screenX, screenY});

    const isAboveHorizon = this.targetPosHelper.y >= camera.position.y;
    if (isAboveHorizon) {
      return;
    }

    const hasHitTest = hitTestTransforms && hitTestTransforms.length > 0;
    if (hasHitTest) {
      this.posHelper.set(hitTestTransforms[0].position.x, hitTestTransforms[0].position.y, hitTestTransforms[0].position.z);
    } else {
      const {newPosition} = raycastCamHeightPoint({
        lastKnownY: this.lastKnownPosHelper.y,
        targetPos: this.targetPosHelper,
        cameraPos: camera.position || POS_SKY,
      });
      this.posHelper.set(newPosition[0], newPosition[1], newPosition[2]);
    }

    this.lastKnownPosHelper.copy(this.posHelper);
    return this.posHelper;
  },
};

export default move;
